import Request from '../../utilities/Request';
import { RETRIEVE_PLAID_ACCOUNTS_BEGIN, RETRIEVE_PLAID_ACCOUNTS_FAIL } from './plaid.js';

/**
 * Editing an account or its institution, through Account_Domain (/api/v1/account/). Each write
 * answers with the whole account list, which replaces state.plaid.plaidAccountsRaw, and with the
 * logos an institution may be given and whether the user is a budget admin, which go to
 * state.plaid.accountOptions (the action's `meta`).
 */
export const ACCOUNT_SAVE_BEGIN = 'ACCOUNT_SAVE_BEGIN';
export const ACCOUNT_SAVE_SUCCESS = 'ACCOUNT_SAVE_SUCCESS';
export const ACCOUNT_SAVE_FAIL = 'ACCOUNT_SAVE_FAIL';
export const ACCOUNT_INDEX_SUCCESS = 'ACCOUNT_INDEX_SUCCESS';

/** What travels beside the list: { logoChoices, isBudgetAdmin }. */
export function accountOptionsFrom(response) {
	return {
		logoChoices: response?.logo_choices || [],
		isBudgetAdmin: !!response?.is_budget_admin,
	};
}

/** The server's own words for a refusal, for the editor to show. */
function refusalMessage(error, fallback = 'The change could not be saved.') {
	return error?.responseJSON?.error || fallback;
}

function post(dispatch, action, data) {
	dispatch({ type: ACCOUNT_SAVE_BEGIN });
	return Request.apiv1Post(`account/${action}`, data)
		.then((response) => {
			dispatch({ type: ACCOUNT_SAVE_SUCCESS, payload: response.accounts, meta: accountOptionsFrom(response) });
			return response;
		})
		.catch((error) => {
			const message = refusalMessage(error);
			dispatch({ type: ACCOUNT_SAVE_FAIL, payload: message });
			throw message;
		});
}

/**
 * `changes` holds only what changed, any of: display_name, type, icon, color, is_active,
 * include_in_total, show_transactions, mask (2 to 6 digits), loan_category_id (a credit or loan
 * account's budget loan category). '' clears display_name, icon, color, mask or loan_category_id.
 */
export const updateAccount = (accountId, changes) => (dispatch) => {
	return post(dispatch, 'update_account', { account_id: accountId, ...changes });
};

/**
 * `institutionId` is the budget's institution (the link's id); `changes` holds any of name, color and
 * logo_institution_id (a plaid institution id such as 'ins_3' from logoChoices). '' clears color or
 * logo_institution_id.
 */
export const updateInstitution = (institutionId, changes) => (dispatch) => {
	return post(dispatch, 'update_institution', { institution_id: institutionId, ...changes });
};

/** `fields`: name, and optionally color and logo_institution_id. */
export const createInstitution = (fields) => (dispatch) => {
	return post(dispatch, 'create_institution', { ...fields });
};

/**
 * `fields`: name, type, and optionally institution_id and mask. With no institution_id the account
 * goes to the budget's "Imported accounts" group, as an import would put it.
 */
export const createAccount = (fields) => (dispatch) => {
	return post(dispatch, 'create_account', { ...fields });
};

/** Puts the account at the end of another of the budget's institutions. */
export const moveAccount = (accountId, institutionId) => (dispatch) => {
	return post(dispatch, 'move_account', { account_id: accountId, institution_id: institutionId });
};

/** Moves every account from `sourceId` into `targetId` and deletes the emptied source. Admins only. */
export const mergeInstitutions = (sourceId, targetId) => (dispatch) => {
	return post(dispatch, 'merge_institutions', { source_id: sourceId, target_id: targetId });
};

/** Deletes an institution with no accounts. Admins only. */
export const deleteInstitution = (institutionId) => (dispatch) => {
	return post(dispatch, 'delete_institution', { institution_id: institutionId });
};

/**
 * What merging account `sourceId` into `targetId` would do, without doing it: Account_Merger's
 * preview ({ source, target, move, balance_collisions, likely_duplicate_transactions,
 * resign_balances, copy_mask, user_preferences_removed }). Changes nothing in the store. Admins only.
 */
export const fetchMergePreview = (sourceId, targetId) => () => {
	return Request.apiv1Get('account/merge_preview', { source_id: sourceId, target_id: targetId })
		.then((response) => response.preview)
		.catch((error) => {
			throw refusalMessage(error, 'The merge could not be previewed.');
		});
};

/**
 * An account's newest transactions, and how many it has in all: { count, first, last, transactions }.
 * Amounts are cents with money in positive. Nothing goes into the store; the editor shows them and
 * lets them go.
 */
export const fetchAccountTransactions = (accountId, limit = 10) => () => {
	return Request.apiv1Get('account/transactions', { account_id: accountId, limit })
		.then((response) => response.transactions)
		.catch((error) => {
			throw refusalMessage(error, 'The transactions could not be loaded.');
		});
};

/**
 * Moves everything on account `sourceId` onto `targetId` and deletes the source. Admins only. The
 * response carries what moved under `merge`, beside the list.
 */
export const mergeAccounts = (sourceId, targetId) => (dispatch) => {
	return post(dispatch, 'merge_accounts', { source_id: sourceId, target_id: targetId });
};

/**
 * The accounts page's list, with its logo choices and the admin flag. Shares plaid/accounts'
 * loading flags, since it is the same list.
 */
export const retrieveAccounts = () => (dispatch) => {
	dispatch({ type: RETRIEVE_PLAID_ACCOUNTS_BEGIN });
	return Request.apiv1Get('account/index')
		.then((response) => {
			dispatch({ type: ACCOUNT_INDEX_SUCCESS, payload: response.accounts, meta: accountOptionsFrom(response) });
		})
		.catch((error) => {
			console.error(error);
			dispatch({ type: RETRIEVE_PLAID_ACCOUNTS_FAIL });
		});
};
